// src/components/teacher/TeacherGamesSummary.js
import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	LinearProgress,
	Skeleton,
	Chip,
	IconButton,
	Tabs,
	Tab,
	Divider,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@mui/material";
import { Eye, Play, CheckCircle, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAcademicYear } from "../../contexts/AcademicYearContext";

const TeacherGamesSummary = ({ supabase, user }) => {
	const { currentAcademicYear } = useAcademicYear();
	const [allCourses, setAllCourses] = useState([]);
	const [inProgressCourses, setInProgressCourses] = useState([]);
	const [completedCourses, setCompletedCourses] = useState([]);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState(0); // Default to In Progress tab
	const [grades, setGrades] = useState({});
	const navigate = useNavigate();

	// Fetch all grades
	useEffect(() => {
		const fetchGrades = async () => {
			try {
				const { data, error } = await supabase.from("grades").select("*");

				if (error) throw error;

				// Convert array to object with id as key for easier lookup
				const gradesObj = {};
				data.forEach((grade) => {
					gradesObj[grade.id] = grade;
				});

				setGrades(gradesObj);
			} catch (error) {
				console.error("Error fetching grades:", error);
			}
		};

		fetchGrades();
	}, [supabase]);

	useEffect(() => {
		const fetchGamesSummary = async () => {
			if (!user) {
				setLoading(false);
				return;
			}

			setLoading(true);
			try {
				// Fetch all available course categories for all grades
				const { data: allCategories, error: categoriesError } = await supabase
					.from("course_categories")
					.select(
						`
						id,
						name,
						description,
						thumbnail,
						concept,
						grade
						`
					)
					.eq("status", true);

				if (categoriesError) throw categoriesError;

				// Fetch the user's progress for all courses
				const { data: progressData, error: progressError } = await supabase
					.from("teacher_course_progress")
					.select("*")
					.eq("teacher_id", user.id)
					.eq("academic_year", currentAcademicYear);

				if (progressError) throw progressError;

				// Create a map of course progress for easier lookup
				const progressMap = progressData?.reduce(
					(acc, progress) => ({
						...acc,
						[`${progress.category_id}-${progress.grade_id}`]: progress,
					}),
					{}
				);

				// Add progress information to each course
				const coursesWithProgress = allCategories.map((category) => {
					const progress = progressMap[`${category.id}-${category.grade}`];
					return {
						...category,
						progress,
					};
				});

				// Separate courses by status
				const inProgress = coursesWithProgress.filter(
					(course) =>
						course.progress && course.progress.completion_percentage < 100
				);

				const completed = coursesWithProgress.filter(
					(course) =>
						course.progress && course.progress.completion_percentage === 100
				);

				setAllCourses(coursesWithProgress);
				setInProgressCourses(inProgress);
				setCompletedCourses(completed);

				// Set default active tab to In Progress if available, otherwise Completed
				if (inProgress.length > 0) {
					setActiveTab(0);
				} else if (completed.length > 0) {
					setActiveTab(1);
				}
			} catch (error) {
				console.error("Error fetching game summary:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchGamesSummary();
	}, [supabase, user, currentAcademicYear]);

	const handleViewCourse = (categoryId, gradeId) => {
		navigate(`/game-data/${categoryId}?grade=${gradeId}`);
	};

	const handleStartCourse = async (categoryId, gradeId) => {
		try {
			const { data, error } = await supabase
				.from("teacher_course_progress")
				.insert([
					{
						teacher_id: user.id,
						category_id: categoryId,
						grade_id: gradeId,
						status: "in_progress",
						academic_year: currentAcademicYear,
					},
				])
				.select()
				.single();

			if (error) throw error;

			// Update the courses array with the new progress
			const updatedCourse = allCourses.find(
				(course) => course.id === categoryId && course.grade === gradeId
			);

			if (updatedCourse) {
				const newUpdatedCourse = { ...updatedCourse, progress: data };

				setAllCourses((prevCourses) =>
					prevCourses.map((course) =>
						course.id === categoryId && course.grade === gradeId
							? newUpdatedCourse
							: course
					)
				);

				setInProgressCourses((prev) => [...prev, newUpdatedCourse]);
			}

			navigate(`/game-data/${categoryId}?grade=${gradeId}`);
		} catch (error) {
			console.error("Error starting course:", error);
		}
	};

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	const handleViewAllGames = () => {
		navigate("/games");
	};

	if (loading) {
		return (
			<Box sx={{ p: 2 }}>
				<Skeleton variant="text" height={40} width="30%" sx={{ mb: 2 }} />
				<Skeleton variant="rectangular" height={200} />
			</Box>
		);
	}

	// Get current tab's courses
	const currentTabCourses =
		activeTab === 0 ? inProgressCourses : completedCourses;

	// Group courses by grade
	const coursesByGrade = currentTabCourses.reduce((acc, course) => {
		const gradeId = course.grade;
		if (!acc[gradeId]) {
			acc[gradeId] = [];
		}
		acc[gradeId].push(course);
		return acc;
	}, {});

	// Sort grades by grade name
	const sortedGradeIds = Object.keys(coursesByGrade).sort((a, b) => {
		const gradeNameA = grades[a]?.name || `Grade ${a}`;
		const gradeNameB = grades[b]?.name || `Grade ${b}`;
		return gradeNameA.localeCompare(gradeNameB);
	});

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2,
				mt: 3,
				border: "1px solid #e5e7eb",
				borderRadius: "8px",
			}}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					My Games
				</Typography>
				<Button
					variant="text"
					onClick={handleViewAllGames}
					sx={{
						color: "#6366f1",
						textTransform: "none",
						fontSize: "0.875rem",
						"&:hover": {
							backgroundColor: "rgba(99, 102, 241, 0.04)",
						},
					}}>
					View All Games
				</Button>
			</Box>

			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				sx={{
					mb: 2,
					borderBottom: 1,
					borderColor: "divider",
					"& .MuiTab-root": {
						textTransform: "none",
						fontWeight: 500,
						minWidth: 100,
					},
					"& .Mui-selected": {
						color: "#6366f1",
						fontWeight: 600,
					},
					"& .MuiTabs-indicator": {
						backgroundColor: "#6366f1",
					},
				}}>
				<Tab label={`In Progress (${inProgressCourses.length})`} />
				<Tab label={`Completed (${completedCourses.length})`} />
			</Tabs>

			{currentTabCourses.length === 0 ? (
				<Box sx={{ textAlign: "center", py: 4 }}>
					<Typography variant="body1" color="text.secondary">
						{activeTab === 0
							? "You don't have any games in progress."
							: "You haven't completed any games yet."}
					</Typography>
					<Button
						variant="contained"
						sx={{
							mt: 2,
							backgroundColor: "#6366f1",
							"&:hover": {
								backgroundColor: "#4f46e5",
							},
						}}
						onClick={handleViewAllGames}>
						Browse All Games
					</Button>
				</Box>
			) : (
				<Box>
					{sortedGradeIds.map((gradeId) => {
						const gradeCourses = coursesByGrade[gradeId];
						const gradeName = grades[gradeId]?.name || `Grade ${gradeId}`;

						return (
							<Box key={gradeId} sx={{ mb: 3 }}>
								<Typography
									variant="subtitle1"
									sx={{
										fontWeight: 600,
										mb: 1,
										color: "#111827",
										display: "flex",
										alignItems: "center",
									}}>
									<Chip
										label={gradeName}
										size="small"
										color="primary"
										sx={{
											mr: 1,
											backgroundColor: "#6366f1",
										}}
									/>
									{gradeCourses.length}{" "}
									{activeTab === 0 ? "In Progress" : "Completed"} Games
								</Typography>

								<TableContainer>
									<Table sx={{ minWidth: 650 }}>
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 600, color: "#111827" }}>
													Name
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#111827" }}>
													Learning Outcome
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#111827" }}>
													Status
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#111827" }}>
													Progress
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#111827" }}>
													Actions
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{gradeCourses.map((course) => {
												// Determine course status
												let status =
													activeTab === 0 ? "In Progress" : "Completed";
												let statusColor =
													activeTab === 0 ? "primary" : "success";

												return (
													<TableRow
														key={`${course.id}-${course.grade}`}
														sx={{
															"&:hover": {
																backgroundColor: "rgba(0, 0, 0, 0.04)",
															},
														}}>
														<TableCell sx={{ fontWeight: 500 }}>
															{course.name}
														</TableCell>
														<TableCell
															sx={{ maxWidth: 300, color: "text.secondary" }}>
															<Typography noWrap variant="body2">
																{course.concept ||
																	course.description ||
																	"Explore this course"}
															</Typography>
														</TableCell>
														<TableCell>
															<Chip
																label={status}
																color={statusColor}
																size="small"
																sx={{
																	fontSize: "0.75rem",
																	height: 24,
																}}
															/>
														</TableCell>
														<TableCell sx={{ width: 180 }}>
															<Box
																sx={{ display: "flex", alignItems: "center" }}>
																<Box sx={{ width: "100%", mr: 1 }}>
																	<LinearProgress
																		variant="determinate"
																		value={
																			course.progress?.completion_percentage ||
																			0
																		}
																		sx={{
																			height: 6,
																			borderRadius: 1,
																			bgcolor: "#e5e7eb",
																			".MuiLinearProgress-bar": {
																				bgcolor: "#6366f1",
																			},
																		}}
																	/>
																</Box>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	{`${
																		course.progress?.completion_percentage || 0
																	}%`}
																</Typography>
															</Box>
														</TableCell>
														<TableCell>
															<Box sx={{ display: "flex", gap: 1 }}>
																<IconButton
																	size="small"
																	color="primary"
																	onClick={() =>
																		handleViewCourse(course.id, course.grade)
																	}
																	sx={{
																		color: "#6366f1",
																		border: "1px solid #e5e7eb",
																		"&:hover": {
																			backgroundColor:
																				"rgba(99, 102, 241, 0.04)",
																			borderColor: "#6366f1",
																		},
																	}}>
																	<Eye size={16} />
																</IconButton>

																{activeTab === 1 ? (
																	<IconButton
																		size="small"
																		disabled
																		sx={{
																			backgroundColor: "#10b981",
																			color: "white",
																			"&:hover": {
																				backgroundColor: "#059669",
																			},
																			"&.Mui-disabled": {
																				backgroundColor: "#10b981",
																				color: "white",
																				opacity: 0.8,
																			},
																		}}>
																		<CheckCircle size={16} />
																	</IconButton>
																) : (
																	<IconButton
																		size="small"
																		onClick={() =>
																			handleViewCourse(course.id, course.grade)
																		}
																		sx={{
																			backgroundColor: "#6366f1",
																			color: "white",
																			"&:hover": {
																				backgroundColor: "#4f46e5",
																			},
																		}}>
																		<Play size={16} />
																	</IconButton>
																)}
															</Box>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>

								{sortedGradeIds.indexOf(gradeId) <
									sortedGradeIds.length - 1 && (
									<Divider sx={{ mt: 2, mb: 3 }} />
								)}
							</Box>
						);
					})}
				</Box>
			)}
		</Paper>
	);
};

export default TeacherGamesSummary;
